import { useState, useEffect, useCallback, useRef } from "react";

export function useAutoDragScroll({ speed = 1, pauseDelay = 2000 } = {}) {
  const [node, setNode] = useState(null);
  const triggerPauseRef = useRef(null);

  const ref = useCallback((element) => {
    setNode(element);
  }, []);

  useEffect(() => {
    const slider = node;
    if (!slider) return;

    let isDown = false;
    let startX;
    let scrollLeft;
    let animationFrameId;
    let isPaused = false;
    let touchPauseTimeout = null;
    let initialized = false;

    // Auto Scroll Loop
    const autoScroll = () => {
      if (!slider) return;
      
      // If not initialized, jump to the start of the middle copy (Copy 2)
      if (!initialized && slider.scrollWidth > 0) {
        slider.scrollLeft = slider.scrollWidth / 3;
        initialized = true;
      }
      
      if (!isPaused && !isDown) {
        // We have 3 copies. A single copy length is scrollWidth / 3.
        const singleWidth = slider.scrollWidth / 3;
        
        // If we reach the start of Copy 3, jump back to start of Copy 2
        if (slider.scrollLeft >= singleWidth * 2) {
          slider.scrollLeft = slider.scrollLeft - singleWidth;
        } 
        // If we reach the start of Copy 1 (0), jump forward to start of Copy 2
        else if (slider.scrollLeft <= 0 && singleWidth > 0) {
          slider.scrollLeft = singleWidth;
        } else {
          slider.scrollLeft += speed;
        }
      }
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    // Start auto scrolling initially
    animationFrameId = requestAnimationFrame(autoScroll);

    // Pause/Resume interactions
    const pauseScroll = () => {
      isPaused = true;
    };

    const resumeScroll = () => {
      isPaused = false;
    };

    // Touch handlers for mobile
    const handleTouchStart = () => {
      pauseScroll();
      if (touchPauseTimeout) clearTimeout(touchPauseTimeout);
    };

    const handleTouchEnd = () => {
      if (touchPauseTimeout) clearTimeout(touchPauseTimeout);
      touchPauseTimeout = setTimeout(() => {
        resumeScroll();
      }, pauseDelay);
    };

    triggerPauseRef.current = () => {
      pauseScroll();
      if (touchPauseTimeout) clearTimeout(touchPauseTimeout);
      touchPauseTimeout = setTimeout(() => {
        resumeScroll();
      }, pauseDelay);
    };

    // Drag to scroll handlers for desktop
    const onMouseDown = (e) => {
      e.stopPropagation();
      isDown = true;
      pauseScroll();
      slider.classList.add("active");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    };

    const onMouseLeave = () => {
      isDown = false;
      slider.classList.remove("active");
      resumeScroll();
    };

    const onMouseUp = () => {
      isDown = false;
      slider.classList.remove("active");
      // Resume scroll only if not hovering (mouseLeave usually handles this, 
      // but in case they mouse up while still hovering, the mouseEnter state keeps it paused)
    };

    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 1.5; // scroll speed multiplier
      
      let newScrollLeft = scrollLeft - walk;
      const singleWidth = slider.scrollWidth / 3;
      
      // Manage looping during drag (3 copies)
      if (newScrollLeft >= singleWidth * 2) {
        newScrollLeft = newScrollLeft - singleWidth;
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = newScrollLeft;
      } else if (newScrollLeft <= 0 && singleWidth > 0) {
        newScrollLeft = newScrollLeft + singleWidth;
        startX = e.pageX - slider.offsetLeft;
        scrollLeft = newScrollLeft;
      }

      slider.scrollLeft = newScrollLeft;
    };

    // Attach listeners
    slider.addEventListener("mouseenter", pauseScroll);
    slider.addEventListener("mouseleave", onMouseLeave);
    slider.addEventListener("mousedown", onMouseDown);
    slider.addEventListener("mouseup", onMouseUp);
    slider.addEventListener("mousemove", onMouseMove);

    // Touch listeners
    slider.addEventListener("touchstart", handleTouchStart, { passive: true });
    slider.addEventListener("touchend", handleTouchEnd, { passive: true });
    slider.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (touchPauseTimeout) clearTimeout(touchPauseTimeout);

      slider.removeEventListener("mouseenter", pauseScroll);
      slider.removeEventListener("mouseleave", onMouseLeave);
      slider.removeEventListener("mousedown", onMouseDown);
      slider.removeEventListener("mouseup", onMouseUp);
      slider.removeEventListener("mousemove", onMouseMove);

      slider.removeEventListener("touchstart", handleTouchStart);
      slider.removeEventListener("touchend", handleTouchEnd);
      slider.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [node, speed, pauseDelay]);

  return {
    ref,
    node,
    triggerPause: () => {
      if (triggerPauseRef.current) triggerPauseRef.current();
    }
  };
}
